import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";

export default function Terms() {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-onyx-900 text-white">
            <Seo
                title="Terms of Service — SRT Finder Marketplace"
                description="SRT Finder Terms of Service. Rules for buying, selling, and trading Dodge SRT Hellcat, Charger, Challenger, Chrysler 300 SRT, and RAM TRX vehicles on the SRT Finder marketplace and community."
                canonicalPath="/terms"
            />
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <Logo size="xs" />
                </div>
            </div>
            <article className="max-w-3xl mx-auto px-5 py-8 space-y-6 text-onyx-300 leading-relaxed">
                <header>
                    <div className="label-caps text-hellcat">Legal</div>
                    <h1 className="headline text-4xl mt-2">Terms of Service</h1>
                    <p className="text-xs text-onyx-500 mt-2">Last updated: February 2026</p>
                </header>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">1. What SRT Finder is</h2>
                    <p>
                        SRT Finder ("we", "us", "the platform") is a specialized buy/sell/trade
                        marketplace and community for high-performance Mopar vehicles — SRT and R/T
                        trim and above (no V6-equipped Chargers, Challengers, or 300s; performance
                        RAMs only). We do not take title to any vehicle, we are not a party to any
                        transaction, and we do not broker deals. We provide the platform, and
                        buyers and sellers deal directly.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">2. Eligibility & account</h2>
                    <p>
                        You must be 18+ to use SRT Finder. You are responsible for the security of
                        your account, the accuracy of your profile, and everything that happens
                        under your account. One human, one account.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">3. Listings & content rules</h2>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Listings must be for SRT, R/T, Hellcat, Redeye, Demon, Super Stock, Scat Pack, Charger/Challenger performance packages, Chrysler 300 SRT8, or performance RAMs (TRX, RHO, Limited).</li>
                        <li>No V6-equipped Chargers/Challengers/300s. No base RAMs.</li>
                        <li>No stolen, salvage-fraud, or otherwise misrepresented vehicles.</li>
                        <li>Photos and descriptions must accurately represent the vehicle being sold.</li>
                        <li>No off-platform payment scams, wire-fraud attempts, or fake escrow.</li>
                        <li>No harassment, hate speech, doxxing, or threats.</li>
                    </ul>
                    <p>
                        We can hide, edit, or remove any listing or post at our discretion, and we
                        can suspend accounts that violate these rules.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">4. Transactions & disputes</h2>
                    <p>
                        You transact at your own risk. We strongly recommend inspecting vehicles in
                        person, using licensed escrow for large sums, and verifying titles before
                        exchanging money. SRT Finder is not responsible for the outcome of any
                        deal, including but not limited to: vehicle condition, title issues,
                        payment fraud, or shipping.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">5. Fees</h2>
                    <p>
                        Basic listing and community use is free during the current phase. Paid
                        promotions (featured listings, dealer subscriptions) may be introduced with
                        notice. Fees, when charged, are non-refundable unless required by law.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">6. Content ownership & license</h2>
                    <p>
                        You keep ownership of everything you post. By posting, you grant SRT Finder
                        a worldwide, royalty-free license to display, distribute, and promote your
                        content on and off the platform for the purpose of operating and marketing
                        the service.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">7. Termination</h2>
                    <p>
                        We can suspend or terminate accounts that break the rules, harass others,
                        or attempt to abuse the platform. You can delete your account at any time
                        by contacting us.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">8. Disclaimers</h2>
                    <p>
                        SRT Finder is provided "as is", without warranties of any kind. We do not
                        guarantee uninterrupted service or the accuracy of listings. To the maximum
                        extent permitted by law, we are not liable for any indirect, incidental,
                        special, or consequential damages arising from your use of the platform.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">9. Changes</h2>
                    <p>
                        We may update these Terms from time to time. Continuing to use the platform
                        after changes constitutes acceptance of the new Terms.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">10. Contact</h2>
                    <p>
                        Questions? Reach us at{" "}
                        <a className="text-hellcat" href="mailto:support@srtfinder.com">
                            support@srtfinder.com
                        </a>
                        .
                    </p>
                </section>

                <div className="pt-6">
                    <Link to="/privacy" className="text-hellcat label-caps text-xs">
                        Read Privacy Policy →
                    </Link>
                </div>
            </article>
        </div>
    );
}
